import React, { useCallback, useState } from 'react';
import { Button, Buttons, CancelButton, DeleteButton, FormMessage, ModalCard } from '~/components';
import { BulkActionSummary, Flow } from '~/components/bulk-action';
import styled from 'styled-components';
import { Card } from '~/components/ModalCard';

const StyledModalCard = styled(ModalCard)`
  ${Card} {
    width: ${({ $width }) => $width};
  }
`;

const getConfirmUI = (variant) =>
  variant === 'delete'
    ? { Component: DeleteButton, defaultText: 'Delete' }
    : { Component: Button, defaultText: 'Confirm' };

export default function BulkActionModal({
  title,
  onClose,
  onComplete,
  onConfirm, // () => Promise<{ summary, rows }>
  renderRow, // required
  variant = 'confirm', // 'confirm' | 'delete'
  confirmText, // optional label override
  errorMessage = 'There was a problem processing your request.',
  children, // ReactNode | ({ isLoading, setCanSubmit }) => ReactNode
  width = '42rem',
}) {
  const [state, setState] = useState({
    step: 'confirmation',
    isLoading: false,
    data: null,
    error: null,
    canSubmit: true,
  });
  const setCanSubmit = useCallback(
    (canSubmit) => setState((s) => (s.canSubmit === canSubmit ? s : { ...s, canSubmit })),
    [],
  );

  const submit = async () => {
    setState((s) => ({ ...s, isLoading: true }));
    try {
      const data = await onConfirm();
      if (data) {
        setState({ step: 'summary', isLoading: false, data, error: null, canSubmit: true });
        onComplete?.();
      } else {
        setState({ step: 'confirmation', isLoading: false, data: null, error: null, canSubmit: true });
      }
    } catch (error) {
      setState({ step: 'error', isLoading: false, data: null, error, canSubmit: true });
    }
  };

  const { Component: ConfirmBtn, defaultText } = getConfirmUI(variant);
  const label = confirmText ?? defaultText;

  return (
    <StyledModalCard title={title} onClose={onClose} $width={width}>
      <Flow step={state.step}>
        <Flow.Step name="confirmation">
          <ModalCard.Body style={{ padding: 0 }}>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1.625rem' }}>
              {typeof children === 'function'
                ? children({
                    isLoading: state.isLoading,
                    setCanSubmit,
                  })
                : children}
            </div>
          </ModalCard.Body>

          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <ConfirmBtn isLoading={state.isLoading} onClick={submit} disabled={!state.canSubmit || state.isLoading}>
                {label}
              </ConfirmBtn>
            </Buttons>
          </ModalCard.Footer>
        </Flow.Step>

        <Flow.Step name="summary">
          <ModalCard.Content onClose={onClose}>
            <BulkActionSummary summary={state.data?.summary} rows={state.data?.rows} renderRow={renderRow} />
          </ModalCard.Content>
        </Flow.Step>

        <Flow.Step name="error">
          <ModalCard.Content onClose={onClose}>
            <FormMessage.Error>{errorMessage}</FormMessage.Error>
          </ModalCard.Content>
        </Flow.Step>
      </Flow>
    </StyledModalCard>
  );
}

BulkActionModal.Body = ModalCard.Body;
BulkActionModal.Footer = ModalCard.Footer;
